/** Reports whether undo/redo are currently available, for UI enablement. */
export interface HistoryState {
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * Generic undo/redo history over immutable state snapshots. Callers record the
 * pre-mutation snapshot before each change; undo/redo swap snapshots against the
 * caller's current state. A batch folds a continuous gesture (e.g. a drag) into
 * a single undo step by keeping only the first snapshot recorded while open.
 */
export class HistoryManager<T> {
  private undoStack: T[] = [];
  private redoStack: T[] = [];
  private batching = false;
  private batchRecorded = false;
  private listeners: Array<(state: HistoryState) => void> = [];

  constructor(private readonly limit = 100) {}

  /** Opens a batch so only the first recorded snapshot until endBatch is kept. */
  beginBatch(): void {
    this.batching = true;
    this.batchRecorded = false;
  }

  endBatch(): void {
    this.batching = false;
    this.batchRecorded = false;
  }

  /** Pushes the pre-mutation snapshot; folded away for later mutations in a batch. */
  record(snapshot: T): void {
    if (this.batching && this.batchRecorded) {
      return;
    }
    this.undoStack.push(snapshot);
    if (this.undoStack.length > this.limit) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    if (this.batching) {
      this.batchRecorded = true;
    }
    this.notify();
  }

  /** Returns the snapshot to restore, moving current onto the redo stack. */
  undo(current: T): T | null {
    const previous = this.undoStack.pop();
    if (previous === undefined) {
      return null;
    }
    this.redoStack.push(current);
    this.notify();
    return previous;
  }

  /** Returns the snapshot to restore, moving current onto the undo stack. */
  redo(current: T): T | null {
    const next = this.redoStack.pop();
    if (next === undefined) {
      return null;
    }
    this.undoStack.push(current);
    this.notify();
    return next;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.batching = false;
    this.batchRecorded = false;
    this.notify();
  }

  getState(): HistoryState {
    return { canUndo: this.undoStack.length > 0, canRedo: this.redoStack.length > 0 };
  }

  subscribe(listener: (state: HistoryState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify(): void {
    const state = this.getState();
    this.listeners.forEach(listener => listener(state));
  }
}
